import React, { useContext, useEffect, useMemo, useState } from "react";
import { GridGenerator } from "@actinc/dls/components";

import _ from "lodash/fp";

import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import { OpenInNew as OpenInNewIcon } from "@actinc/dls/icons";

import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormDateTime,
  ControlledFormDesktopDatePicker,
  ControlledFormEditor,
  ControlledFormSelect,
  ControlledFormSwitch,
  ExpandMoreIcon,
  PreviewContent,
} from "../../../components";

import { TaskDetailsDialog } from "../../Dashboard/Tasks/TaskDetailsDialog";

import { useStore } from "../../../services/hooks";
import {
  getCompareVersions,
  getTaskPermissions,
  getUserOptions,
  PREVIEW_TYPES,
  sortInactiveTasksByCompletedDate,
} from "../../../services/utils";

import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";
import { Link } from "react-router-dom";
import {
  READY_COMPONENT_TYPE,
  useReadyContext,
} from "../../../providers/ReadyProvider";

// Completed tasks additionally offer the in-app CDE preview.
const TASK_PREVIEW_TYPES = [...PREVIEW_TYPES, "CDE"];

const TasksCard = ({ activeTasks, objectType }) => {
  const [object] = useStore(objectType);

  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);
  const { markReady } = useReadyContext();

  const { control } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: `${objectType}.tasks`,
  });

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks[objectType]),
    [activeTasks, objectType],
  );

  const tasks = useMemo(
    () =>
      fields?.reduce(
        (acc, task) =>
          activeTasks[objectType] && task._actions.includes("edit")
            ? { ...acc, active: [task, ...acc.active] }
            : { ...acc, inactive: [...acc.inactive, task] },
        { active: [], inactive: [] },
      ),
    [activeTasks, objectType, fields],
  );

  const [taskDetailsDialogState, setTaskDetailsDialogState] = useState({
    open: false,
  });

  const [expanded, setExpanded] = useState([]);
  useEffect(() => {
    if (objectType === "passage") {
      markReady([], READY_COMPONENT_TYPE.PASSAGE);
    } else if (objectType === "item") {
      markReady([], READY_COMPONENT_TYPE.ITEM);
    }
  }, []);

  if (!object || !fields.length) return null;

  const handleAccordion = (id) => () => {
    if (expanded.includes(id)) {
      setExpanded(expanded.filter((i) => i !== id));
    } else {
      setExpanded([...expanded, id]);
    }
  };

  const handleExpandAll = () => {
    setExpanded([...tasks.inactive].map(({ id }) => id));
  };

  const handleCollapseAll = () => {
    setExpanded([]);
  };

  const openTaskDetailsDialog = (task) => () => {
    setTaskDetailsDialogState({
      onClose: () => setTaskDetailsDialogState({ open: false }),
      open: true,
      objectType: object.__typename,
      row: {
        contentArea: object.contentArea,
        gradeLevel: object.gradeLevel,
        imageDetails: task.imageDetails,
        itemFormat: object.format,
        name: object.name,
        program: programs.find((program) => program.id === task.program)?.name,
        requestedDateTime: new Date(task.requestedDateTime),
        taskName: task.name,
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
        }}
      >
        {tasks.active.map((task) => {
          const index = fields.findIndex((o) => o.id === task.id);
          const namePrefix = `${objectType}.tasks[${index}]`;
          return (
            <Box key={task.id}>
              {!getTaskPermissions(task).includes("externalReview") && (
                <Paper
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "secondary.contrastText",
                    marginX: -2,
                    paddingLeft: 2,
                  }}
                >
                  <Toolbar disableGutters={true}>
                    <Typography sx={{ width: "20em" }} variant="body1">
                      {task.name}
                    </Typography>
                    <Typography
                      sx={{
                        opacity: "0.67",
                      }}
                      variant="body1"
                    >
                      {task.developmentState || ""}
                    </Typography>
                  </Toolbar>
                </Paper>
              )}
              <GridGenerator
                groups={[
                  [
                    {
                      children: null,
                      key: `preview`,
                    },
                    {
                      children: task.imageDetails && (
                        <IconButton
                          aria-label="Task Details"
                          onClick={openTaskDetailsDialog(task)}
                          title="Task Details"
                        >
                          <SpeakerNotesIcon />
                        </IconButton>
                      ),
                      key: `details`,
                    },
                  ],
                  [
                    {
                      children: (
                        <ControlledFormEditor
                          control={control}
                          fullWidth={true}
                          id={`${namePrefix}.notes`}
                          label="Comments"
                          name={`${namePrefix}.notes`}
                          options={["taskComments"]}
                        />
                      ),
                      key: `notes`,
                    },
                  ],
                  [
                    {
                      children: getTaskPermissions(task)?.includes(
                        "showQRR",
                      ) && (
                        <ControlledFormSelect
                          control={control}
                          fullWidth={true}
                          id={`${namePrefix}.qrr`}
                          label="Quality Rubric Rating"
                          name={`${namePrefix}.qrr`}
                          options={qrrOptions}
                        />
                      ),
                      key: `qrr`,
                    },
                    {
                      children: (
                        <ControlledFormSwitch
                          control={control}
                          fullWidth={true}
                          id={`${namePrefix}.ready`}
                          key={`${namePrefix}.ready`}
                          label={`Ready to Complete`}
                          name={`${namePrefix}.ready`}
                        />
                      ),
                      key: `ready`,
                    },
                  ],
                  [
                    {
                      children:
                        task?.taskDefinition?.creation?.details?.includes(
                          "instructions",
                        ) && (
                          <Box sx={{ width: "100%" }}>
                            <Typography variant="subtitle2">
                              Instructions
                            </Typography>
                            <Box
                              dangerouslySetInnerHTML={{
                                __html: task.instructions,
                              }}
                            />
                          </Box>
                        ),
                      key: `instructions`,
                    },
                  ],
                  [
                    {
                      children: (
                        <ControlledFormDateTime
                          control={control}
                          fullWidth={true}
                          id={`${namePrefix}.createdDateTime`}
                          label="Created"
                          name={`${namePrefix}.createdDateTime`}
                          readonly={true}
                        />
                      ),
                      key: `createdDateTime`,
                    },
                    {
                      children: (
                        <ControlledFormSelect
                          control={control}
                          fullWidth={true}
                          id={`${namePrefix}.assignedUser`}
                          label="Assigned To"
                          name={`${namePrefix}.assignedUser`}
                          options={getUserOptions(users)({
                            activeOnly: false,
                          })}
                          readonly={true}
                        />
                      ),
                      key: `assignedUser`,
                    },
                  ],
                  [
                    {
                      children:
                        task?.taskDefinition?.creation?.details?.includes(
                          "dueDate",
                        ) && (
                          <ControlledFormDesktopDatePicker
                            control={control}
                            fullWidth={true}
                            id={`${namePrefix}.dueDate`}
                            label="Due Date"
                            name={`${namePrefix}.dueDate`}
                            readonly={true}
                          />
                        ),
                      key: `dueDate`,
                    },
                  ],
                ]}
              />
            </Box>
          );
        })}

        {!permissions.includes("externalReview") && !!tasks.inactive.length && (
          <>
            {" "}
            <div>
              <Tooltip
                title={
                  expanded.length === tasks.inactive.length
                    ? "Collapse All"
                    : "Expand All"
                }
              >
                <IconButton
                  onClick={
                    expanded.length === tasks.inactive.length
                      ? handleCollapseAll
                      : handleExpandAll
                  }
                  sx={{
                    display: "block",
                    fontSize: "large",
                    marginTop: "20px",
                    marginRight: "auto",
                    marginLeft: "auto",
                  }}
                >
                  {expanded.length === tasks.inactive.length ? (
                    <KeyboardDoubleArrowUpIcon />
                  ) : (
                    <KeyboardDoubleArrowDownIcon />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            {sortInactiveTasksByCompletedDate(tasks.inactive).map((task) => {
              const index = fields.findIndex((o) => o.id === task.id);
              const namePrefix = `${objectType}.tasks[${index}]`;
              const taskId = object.tasks.find((t) => t.name === task.name)?.id;
              return (
                <Box key={task.id}>
                  <Accordion
                    expanded={expanded.includes(task.id)}
                    onChange={handleAccordion(task.id)}
                    sx={{
                      "& .MuiAccordionSummary-expandIconWrapper button": {
                        color: "secondary.contrastText",
                        opacity: 1,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      id={`${namePrefix}.expandIcon`}
                      sx={{
                        backgroundColor: "secondary.main",
                        color: "secondary.contrastText",
                        marginX: -2,
                      }}
                    >
                      <Typography sx={{ width: "20em" }}>
                        {task.name}
                      </Typography>
                      <Typography
                        sx={{
                          opacity: "0.67",
                        }}
                      >
                        {task.developmentState || ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <GridGenerator
                        groups={[
                          [
                            {
                              children: !!task.snapshotVersionId && (
                                <PreviewContent
                                  compareOptions={getCompareVersions({
                                    task,
                                    completedTasks: tasks.inactive,
                                    latestVersionId: object.versionID,
                                  })}
                                  objects={[
                                    {
                                      id: object.id,
                                      versionID: task.snapshotVersionId,
                                    },
                                  ]}
                                  objectType={object.__typename}
                                  previewTypes={TASK_PREVIEW_TYPES}
                                />
                              ),
                              key: `preview`,
                            },
                            {
                              children: task.imageDetails && (
                                <>
                                  <IconButton
                                    aria-label="Task Details"
                                    onClick={openTaskDetailsDialog(task)}
                                    title="Task Details"
                                  >
                                    <SpeakerNotesIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="Task Details"
                                    component={Link}
                                    to={`/task-details/${objectType}/${object.id}/${taskId}?program=${object.program.name}`}
                                    target="_blank"
                                    title="Open in a new window"
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                </>
                              ),
                              key: `details`,
                            },
                          ],
                          [
                            {
                              children: (
                                <>
                                  <Typography variant="subtitle2">
                                    Comments
                                  </Typography>
                                  <Box
                                    sx={{
                                      border: "1px solid darkgrey",
                                    }}
                                  >
                                    <Box
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          task.notes ||
                                          `<p style="color: lightgrey;">No comment</p>`,
                                      }}
                                      sx={{ margin: 1 }}
                                    />
                                  </Box>
                                </>
                              ),
                              key: `notes`,
                            },
                          ],
                          [
                            {
                              children: getTaskPermissions(task)?.includes(
                                "showQRR",
                              ) && (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  id={`${namePrefix}.qrr`}
                                  label="Quality Rubric Rating"
                                  name={`${namePrefix}.qrr`}
                                  options={qrrOptions}
                                  readonly={true}
                                />
                              ),
                              key: `qrr`,
                            },
                            {
                              children: (
                                <ControlledFormSwitch
                                  control={control}
                                  fullWidth={true}
                                  id={`${namePrefix}.ready`}
                                  key={`${namePrefix}.ready`}
                                  label={`Ready to Complete`}
                                  name={`${namePrefix}.ready`}
                                  readonly={true}
                                />
                              ),
                              key: `ready`,
                            },
                          ],
                          [
                            {
                              children:
                                task?.taskDefinition?.creation?.details?.includes(
                                  "instructions",
                                ) && (
                                  <Box sx={{ width: "100%" }}>
                                    <Typography variant="subtitle2">
                                      Instructions
                                    </Typography>
                                    <Box
                                      dangerouslySetInnerHTML={{
                                        __html: task.instructions,
                                      }}
                                    />
                                  </Box>
                                ),
                              key: `instructions`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormDateTime
                                  control={control}
                                  fullWidth={true}
                                  id={`${namePrefix}.createdDateTime`}
                                  label="Created"
                                  name={`${namePrefix}.createdDateTime`}
                                  readonly={true}
                                />
                              ),
                              key: `createdDateTime`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  id={`${namePrefix}.assignedUser`}
                                  label="Assigned To"
                                  name={`${namePrefix}.assignedUser`}
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                  })}
                                  readonly={true}
                                />
                              ),
                              key: `assignedUser`,
                            },
                          ],
                          [
                            {
                              children:
                                task?.taskDefinition?.creation?.details?.includes(
                                  "dueDate",
                                ) && (
                                  <ControlledFormDesktopDatePicker
                                    control={control}
                                    fullWidth={true}
                                    id={`${namePrefix}.dueDate`}
                                    label="Due Date"
                                    name={`${namePrefix}.dueDate`}
                                    readonly={true}
                                  />
                                ),
                              key: `dueDate`,
                            },
                            {
                              children: task.completedDateTime && (
                                <ControlledFormDateTime
                                  control={control}
                                  fullWidth={true}
                                  id={`${namePrefix}.completedDateTime`}
                                  label="Completed"
                                  name={`${namePrefix}.completedDateTime`}
                                  readonly={true}
                                />
                              ),
                              key: `completedDateTime`,
                            },
                          ],
                        ]}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </>
        )}
      </Box>
      {taskDetailsDialogState.open && (
        <TaskDetailsDialog {...taskDetailsDialogState} />
      )}
    </>
  );
};

const qrrOptions = [
  {
    label: "1 (extensive defects - reject without opportunity to revise)",
    value: "1 (extensive defects - reject without opportunity to revise)",
  },
  {
    label: "2 (major defects - reject requesting revision)",
    value: "2 (major defects - reject requesting revision)",
  },
  {
    label: "3 (minor defects - accept and revise)",
    value: "3 (minor defects - accept and revise)",
  },
  {
    label: "4 (no defects - item must meet all criteria listed to receive a 4)",
    value: "4 (no defects - item must meet all criteria listed to receive a 4)",
  },
];

export { TasksCard };
